import React, { useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import moment from 'moment';
import Compressor from 'compressorjs';

import api from '../../../services/ApiManagement/Index';
import { useToast } from '../../../hooks/toast';
import { useOnlineStatus } from '../../../hooks/onlineStatus';
import getValidationErrors from '../../../utils/getValidationErrors';

import BackHeader from '../../../components/BackHeader';
import Input from '../../../components/Form/Input';
import Button from '../../../components/Button';
import Accordion from '../../../components/Accordion';
import RadioInput from '../../../components/Form/RadioInput';
import RelativeInput from '../../../components/Animal/RelativeInput';
import SwitchInput from '../../../components/Form/SwitchInput';
import Select from '../../../components/Form/Select';
import ImageInput from '../../../components/Animal/ImageInput';

import { Container, Content, DateInput } from './styles';
import { Label } from '../../../components/Generic';

interface NewAnimalData {
  sexo: 'macho' | 'femea';
  numero_de_identificacao: string;
  ano: string;
  mes: string;
  dia: string;
  botom_eletronico: string;
  nome: string;
  ra_a: string;
  categoria: string;
  obs: string;
  lote: string;
  pertencente: boolean;
  privado: boolean;
  images: any[];
  pai_id: string;
  mae_id: string;
  safra: string;
}

const NewAnimal: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [images, setImages] = useState<any>([]);

  const formRef = useRef<FormHandles>(null);
  const history = useHistory();
  const isOnline = useOnlineStatus();
  const { addToast } = useToast();
  const { id_property }: any = useParams();

  const handleSubmit = async (data: NewAnimalData) => {
    try {
      setLoading(true);

      formRef.current?.setErrors({});

      const schema = Yup.object().shape({
        sexo: Yup.string().required('Campo obrigatório.'),
        ano: Yup.string().required('Campo obrigatório.'),

        ...(!data.nome && {
          numero_de_identificacao: Yup.string().required('Campo obrigatório.'),
        }),
      });

      await schema.validate(data, {
        abortEarly: false,
      });

      const animal = await api.post(
        `/animais`,
        {
          categoria: data.categoria,
          data_de_nascimento: `${data.mes} ${data.dia} ${data.ano}`,
          nome: data.nome,
          sexo: data.sexo,
          raça: data.ra_a,
          bottom_eletronico:
            data.botom_eletronico.length === 0
              ? undefined
              : data.botom_eletronico,
          numero_de_identificacao:
            data.numero_de_identificacao.length === 0
              ? undefined
              : data.numero_de_identificacao,
          pertencente: data.pertencente,
          privado: data.privado,
          lote: data.lote,
          safra: data.safra,
          pai_id: data.pai_id.length === 0 ? undefined : data.pai_id,
          mae_id: data.mae_id.length === 0 ? undefined : data.mae_id,
          propriedade_id: id_property,
        },
        isOnline,
        {
          headers: {
            propriedade_id: id_property,
          },
        },
      );

      addToast({
        type: 'success',
        title: 'Sucesso!',
        description: 'O animal foi criado.',
      });

      if (data.mae_id.length !== 0 && isOnline) {
        await api.post(
          `/registros/${data.mae_id}`,
          {
            animal_id: data.mae_id,
            data: `${data.ano}-${data.mes}-${data.dia} ${moment().format(
              'HH:mm',
            )}`,
            categoria: 'paricao',
            descricao: 'Registro gerado automaticamente pelo sistema',
            animalCategoria_id: animal.data.id,
          },
          isOnline,
          {
            headers: {
              propriedade_id: id_property,
            },
          },
        );
      }

      if (files.length > 0) {
        addToast({
          type: 'info',
          title: 'Upload',
          description: 'Fazendo o upload de imagens.',
        });

        const imageData = new FormData();
        files.map(image => {
          new Compressor(image, {
            quality: 0.6,
            maxHeight: 700,
            maxWidth: 700,
            success(resultBlob) {
              const compressedFile = new File([resultBlob], image.name, {
                type: 'image/jpeg',
              });

              imageData.append('imagens', compressedFile);
            },
            error(err) {
              addToast({
                type: 'error',
                title: 'Erro!',
                description: 'Não foi possível comprimir as imagens.',
              });
            },
          });
        });

        await api.patch(
          `/imagens/animal/${animal.data.id}`,
          imageData,
          isOnline,
          {
            headers: {
              propriedade_id: id_property,
            },
          },
        );

        addToast({
          type: 'success',
          title: 'Sucesso!',
          description: 'Imagem adicionada ao animal.',
        });
      }

      if (isOnline) {
        history.replace(
          `/propriedade/${id_property}/animais/${animal.data.id}`,
        );
      } else {
        history.goBack();
      }
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const errors = getValidationErrors(err);

        formRef.current?.setErrors(errors);

        const element = document.getElementById(err.inner[0].path || '');

        if (element !== null) {
          window.scrollTo(0, element.offsetTop - 90);
        }

        return;
      }

      console.log(err);

      addToast({
        type: 'error',
        title: 'Erro!',
        description: err.response?.data?.message || 'Algo deu errado!',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddImage = (e: any) => {
    setFiles(files?.concat(e.target.files[0]));
    setImages(
      images?.concat({
        image_url: URL.createObjectURL(e.target.files[0]),
        id: e.target.files[0].name,
      }),
    );
  };

  const removeImage = (imageId: any) => {
    const filteredFiles = files.filter(file => file.name !== imageId);
    setFiles(filteredFiles);

    const filteredImages = filteredFiles.map(file => {
      return {
        image_url: URL.createObjectURL(file),
        id: file.name,
      };
    });
    setImages(filteredImages);
  };

  const radioOptions = [
    { id: 'macho', value: 'macho', label: 'Macho' },
    { id: 'femea', value: 'femea', label: 'Fêmea' },
  ];

  const categoryOptions = [
    { id: 'Bezerro', value: 'Bezerro', label: 'Bezerro' },
    { id: 'Novilho', value: 'Novilho', label: 'Novilho' },
    { id: 'Vaca', value: 'Vaca', label: 'Vaca' },
    { id: 'Touro', value: 'Touro', label: 'Touro' },
    { id: 'Boi', value: 'Boi', label: 'Boi' },
    { id: 'Outros', value: 'Outros', label: 'Outros' },
  ];

  return (
    <Container>
      <BackHeader title="Novo animal" />

      <Content>
        <Form ref={formRef} onSubmit={handleSubmit}>
          <ImageInput
            images={images}
            onAddImage={handleAddImage}
            removeImage={removeImage}
          />

          <Input
            label="Número de identificação"
            name="numero_de_identificacao"
            type="number"
          />
          <Input label="Botom eletrônico" name="botom_eletronico" type="text" />

          <Label>Nascimento</Label>

          <DateInput>
            <Input
              name="dia"
              label="Dia"
              type="number"
              step="1"
              min="1"
              max="31"
              defaultValue={moment().format('DD')}
            />
            <Input
              name="mes"
              label="Mês"
              type="number"
              step="1"
              min="1"
              max="12"
              defaultValue={moment().format('MM')}
            />
            <Input
              name="ano"
              label="Ano"
              type="number"
              step="1"
              required={true}
              defaultValue={moment().format('YYYY')}
            />
          </DateInput>

          <Input label="Safra" name="safra" type="text" />

          <RadioInput
            direction="row"
            name="sexo"
            label="Sexo"
            options={radioOptions}
            required={true}
          />

          <Input label="Nome" name="nome" type="text" />
          <Input label="Raça" name="ra_a" type="text" />

          <Select label="Categoria" name="categoria">
            <option value="" disabled selected>
              Selecionar
            </option>
            {categoryOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>

          <Input label="Lote" name="lote" type="text" />

          <Accordion label="Pai">
            <RelativeInput
              path="pai_id"
              property_id={id_property}
              onClear={() => {
                formRef.current?.reset();
                window.scrollTo(0, 0);
              }}
            />
          </Accordion>

          <Accordion label="Mãe">
            <RelativeInput
              path="mae_id"
              property_id={id_property}
              onClear={() => {
                formRef.current?.reset();
                window.scrollTo(0, 0);
              }}
            />
          </Accordion>

          <SwitchInput
            label="Animal pertence ao rebanho"
            name="pertencente"
            defaultChecked={true}
          />

          <SwitchInput
            label="Animal privado"
            name="privado"
            defaultChecked={false}
          />

          <Button type="submit" isLoading={loading} style={{ marginTop: 60 }}>
            Salvar
          </Button>
        </Form>
      </Content>
    </Container>
  );
};

export default NewAnimal;
